import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, ChevronsUpDown, Plus, Trash2, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/utils/formatCurrency";
import { updateCustomer } from "@/integrations/supabase/customers";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentEmpresaId } from "@/integrations/supabase/auth-utils";
import { listServicos, type ServicoRow } from "@/integrations/supabase/servicos";
import { cn } from "@/lib/utils";
import { CFOPsMaisComuns, getDescricaoCFOP, isValidarCFOP } from "@/utils/cfopConstants";
import { CSTsMaisComuns, getDescricaoCST, isValidarCST } from "@/utils/cstConstants";
import { IPISituacoesMaisComuns, getDescricaoIPISituacao, isValidarIPISituacao } from "@/utils/ipiConstants";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

// ============================================================================
// REFORMA TRIBUTÁRIA - FUNÇÃO AUXILIAR DE CÁLCULO IBS/CBS
// ============================================================================
// Calcula automaticamente IBS/CBS para valores conforme alíquotas 2026
function calcularIbsCbsHelper(valorItem: number): Record<string, any> {
  if (!valorItem || valorItem <= 0) {
    return {
      cbs_aliquota: 0.009,
      cbs_valor: 0,
      ibs_uf_aliquota: 0.001,
      ibs_uf_valor: 0,
      ibs_mun_aliquota: 0,
      ibs_mun_valor: 0,
      ibs_valor_total: 0
    };
  }

  // Alíquotas 2026 conforme Reforma Tributária
  const CBS_ALIQUOTA = 0.009;        // 0.9% - FEDERAL
  const IBS_UF_ALIQUOTA = 0.001;     // 0.1% - ESTADUAL
  const IBS_MUN_ALIQUOTA = 0;        // 0% - MUNICIPAL em 2026

  const cbsValor = parseFloat((valorItem * CBS_ALIQUOTA).toFixed(2));
  const ibsUfValor = parseFloat((valorItem * IBS_UF_ALIQUOTA).toFixed(2));
  const ibusMunValor = parseFloat((valorItem * IBS_MUN_ALIQUOTA).toFixed(2));
  const ibsValorTotal = parseFloat((ibsUfValor + ibusMunValor).toFixed(2));

  return {
    ibs_cbs_situacao_tributaria: '00',
    ibs_cbs_classificacao_tributaria: '0000000',
    ibs_cbs_base_calculo: parseFloat(valorItem.toFixed(2)),
    cbs_aliquota: CBS_ALIQUOTA,
    cbs_valor: cbsValor,
    ibs_uf_aliquota: IBS_UF_ALIQUOTA,
    ibs_uf_valor: ibsUfValor,
    ibs_mun_aliquota: IBS_MUN_ALIQUOTA,
    ibs_mun_valor: ibusMunValor,
    ibs_valor_total: ibsValorTotal
  };
}

interface NotaItem {
  numero_item: number;
  codigo_produto: string;
  descricao: string;
  ncm: string;
  cfop: string;
  icms_situacao_tributaria?: string;  // CST - Situação Tributária ICMS
  ipi_situacao_tributaria?: string;   // Situação de IPI (01=Tributado, 02=Isento, etc)
  ipi_aliquota?: number;              // Alíquota IPI em %
  unidade: string;
  quantidade: number;
  valor_unitario: number;
  valor_total: number;

  // NOVOS CAMPOS - REFORMA TRIBUTÁRIA (IBS/CBS)
  // Estes campos são opcionais e calculados automaticamente
  ibs_cbs_situacao_tributaria?: string;
  ibs_cbs_classificacao_tributaria?: string;
  ibs_cbs_base_calculo?: number;
  cbs_aliquota?: number;
  cbs_valor?: number;
  ibs_uf_aliquota?: number;
  ibs_uf_valor?: number;
  ibs_mun_aliquota?: number;
  ibs_mun_valor?: number;
  ibs_valor_total?: number;
  
  // ✅ NOVOS CAMPOS - DADOS FISCAIS DO PRODUTO
  // Estes campos vêm dos dados fiscais do produto no catálogo
  cest_code?: string;
  icms_origem?: string;
  icms_aliquota?: number;
  pis_aliquota?: number;
  cofins_aliquota?: number;
  ipi_aliquota?: number;
}

export interface ClienteData {
  id?: string;
  nome: string;
  cpf_cnpj?: string;
  email?: string;
  telefone?: string;
  inscricao_estadual?: string;
  ie_isento?: boolean;
  endereco?: {
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
  };
}

interface DialogEmitirNotaProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEmitir: (
    items: NotaItem[],
    tipoNota: 'NFe' | 'NFCe' | 'NFSe',
    cliente: ClienteData,
    nfse?: {
      prestadorCodigoMunicipio: string;
      prestadorInscricaoMunicipal?: string;
      tomadorCodigoMunicipio: string;
      codigoMunicipioPrestacao?: string;
      itemListaServico: string;
      codigoTributarioMunicipio?: string;
      aliquota: number;
      issRetido: boolean;
      discriminacao: string;
      naturezaOperacao?: number;
      regimeEspecialTributacao?: number;
      optanteSimplesNacional?: boolean;
      incentivadorCultural?: boolean;
      baseCalculoPisCofins?: number | string;
      aliquotaPis?: number | string;
      aliquotaCofins?: number | string;
      valorPis?: number | string;
      valorCofins?: number | string;
      tipoRetencaoPisCofins?: number | string;
      valorCsll?: number | string;
      valorIrrf?: number | string;
    }
  ) => Promise<void>;
  valorTotalPadrao?: number;
  orderCode?: string;
  loading?: boolean;
  clienteInicial?: ClienteData;
  // 📅 NOVOS PARÂMETROS: Items do pedido para pré-preenchimento
  itensPedido?: Array<{
    person_name?: string;
    size?: string | null;
    quantity?: number;
    notes?: string | null;
  }>;
  // 📝 Descrição do pedido (para parsing dos itens)
  descricaoPedido?: string;
  // ✅ NOVO: Items com dados fiscais dos produtos do catálogo
  itensPedidoComFiscal?: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    fiscal_data?: {
      ncm_code?: string;
      cest_code?: string;
      icms_origem?: string;
      icms_aliquota?: number;
      pis_aliquota?: number;
      cofins_aliquota?: number;
      ipi_aliquota?: number;
    };
  }>;
}

export function DialogEmitirNota({
  open,
  onOpenChange,
  onEmitir,
  valorTotalPadrao = 0,
  orderCode = '',
  loading = false,
  clienteInicial,
  itensPedido = [], // 📅 Items do pedido para pré-preenchimento
  descricaoPedido = '', // 📝 Descrição do pedido para parsing
  itensPedidoComFiscal = [] // ✅ NOVO: Items com dados fiscais dos produtos
}: DialogEmitirNotaProps) {
  const itemsPrefillDoneRef = useRef(false);
  const userEditedItemsRef = useRef(false);

  const [tipoNota, setTipoNota] = useState<'NFe' | 'NFCe' | 'NFSe'>('NFe');
  const [valorUnitarioDrafts, setValorUnitarioDrafts] = useState<Record<number, string>>({});
  const [items, setItems] = useState<NotaItem[]>([
    {
      numero_item: 1,
      codigo_produto: orderCode || '1',
      descricao: `Pedido ${orderCode}` || 'Produto',
      ncm: '6204.62.00', // NCM padrão para confecções
      cfop: '5102', // CFOP padrão para venda consumidor final
      unidade: 'UN',
      quantidade: 1,
      valor_unitario: valorTotalPadrao,
      valor_total: valorTotalPadrao,
      // NOVOS CAMPOS - REFORMA TRIBUTÁRIA (IBS/CBS)
      ...calcularIbsCbsHelper(valorTotalPadrao)
    }
  ]);

  // Estado para dados do cliente
  const [cliente, setCliente] = useState<ClienteData>({
    nome: clienteInicial?.nome || '',
    cpf_cnpj: clienteInicial?.cpf_cnpj || '',
    email: clienteInicial?.email || '',
    telefone: clienteInicial?.telefone || '',
    inscricao_estadual: clienteInicial?.inscricao_estadual || '',
    ie_isento: clienteInicial?.ie_isento || false,
    endereco: {
      logradouro: clienteInicial?.endereco?.logradouro || '',
      numero: clienteInicial?.endereco?.numero || '',
      complemento: clienteInicial?.endereco?.complemento || '',
      bairro: clienteInicial?.endereco?.bairro || '',
      cidade: clienteInicial?.endereco?.cidade || '',
      uf: clienteInicial?.endereco?.uf || '',
      cep: clienteInicial?.endereco?.cep || '',
    }
  });

  // Adicione um estado para id do cliente, se disponível
  const [clienteId, setClienteId] = useState<string | null>(clienteInicial?.id || null);

  type ClienteOption = {
    id: string;
    name: string;
    phone?: string | null;
    email?: string | null;
    cpf_cnpj?: string | null;
    inscricao_estadual?: string | null;
    ie_isento?: boolean | null;
    endereco_logradouro?: string | null;
    endereco_numero?: string | null;
    endereco_complemento?: string | null;
    endereco_bairro?: string | null;
    endereco_cidade?: string | null;
    endereco_uf?: string | null;
    endereco_cep?: string | null;
  };

  const [clientesDisponiveis, setClientesDisponiveis] = useState<ClienteOption[]>([]);
  const [isLoadingClientes, setIsLoadingClientes] = useState(false);
  const [clienteSelecionadoId, setClienteSelecionadoId] = useState<string>(clienteInicial?.id || "");
  const [clientePopoverOpen, setClientePopoverOpen] = useState(false);
  const [clienteSearchTerm, setClienteSearchTerm] = useState("");
  const [servicos, setServicos] = useState<ServicoRow[]>([]);
  const [servicoSelecionadoId, setServicoSelecionadoId] = useState<string>("");
  const [servicoPopoverOpen, setServicoPopoverOpen] = useState(false);
  const [servicoSearchTerm, setServicoSearchTerm] = useState("");

  const [nfseDados, setNfseDados] = useState({
    prestadorCodigoMunicipio: '',
    prestadorInscricaoMunicipal: '',
    tomadorCodigoMunicipio: '',
    codigoMunicipioPrestacao: '',
    itemListaServico: '',
    codigoTributarioMunicipio: '',
    aliquota: '',
    issRetido: false,
    naturezaOperacao: '1',
    regimeEspecialTributacao: '',
    optanteSimplesNacional: true,
    incentivadorCultural: false,
    discriminacao: '',
  });

  // Reset dos flags sempre que o modal fecha
  useEffect(() => {
    if (!open) {
      itemsPrefillDoneRef.current = false;
      userEditedItemsRef.current = false;
      setValorUnitarioDrafts({});
    }
  }, [open]);

  // Atualizar dados do cliente quando clienteInicial mudar
  useEffect(() => {
    if (clienteInicial && open) {
      setClienteId(clienteInicial.id || null);
      setClienteSelecionadoId(clienteInicial.id || "");
      setCliente({
        nome: clienteInicial.nome || '',
        cpf_cnpj: clienteInicial.cpf_cnpj || '',
        email: clienteInicial.email || '',
        telefone: clienteInicial.telefone || '',
        inscricao_estadual: clienteInicial.inscricao_estadual || '',
        ie_isento: clienteInicial.ie_isento || false,
        endereco: {
          logradouro: clienteInicial.endereco?.logradouro || '',
          numero: clienteInicial.endereco?.numero || '',
          complemento: clienteInicial.endereco?.complemento || '',
          bairro: clienteInicial.endereco?.bairro || '',
          cidade: clienteInicial.endereco?.cidade || '',
          uf: clienteInicial.endereco?.uf || '',
          cep: clienteInicial.endereco?.cep || '',
        }
      });
      // Buscar do banco se não vier preenchido
      if (clienteInicial.id && (!clienteInicial.inscricao_estadual || clienteInicial.ie_isento === undefined)) {
        supabase.from('customers').select('inscricao_estadual,ie_isento').eq('id', clienteInicial.id).single().then(({ data }) => {
          if (data) {
            setCliente(prev => ({ ...prev, inscricao_estadual: data.inscricao_estadual || '', ie_isento: !!data.ie_isento }));
          }
        });
      }
    }
  }, [clienteInicial, open]);

  useEffect(() => {
    const carregarClientes = async () => {
      if (!open) return;
      setIsLoadingClientes(true);
      try {
        const empresaId = await getCurrentEmpresaId();
        const { data, error } = await supabase
          .from('customers')
          .select('id,name,phone,email,cpf_cnpj,inscricao_estadual,ie_isento,endereco_logradouro,endereco_numero,endereco_complemento,endereco_bairro,endereco_cidade,endereco_uf,endereco_cep')
          .eq('empresa_id', empresaId)
          .order('name', { ascending: true });

        if (error) {
          console.error('Erro ao carregar clientes:', error);
          toast.error('Não foi possível carregar os clientes');
          return;
        }
        setClientesDisponiveis(data || []);
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
        toast.error('Não foi possível carregar os clientes');
      } finally {
        setIsLoadingClientes(false);
      }
    };

    carregarClientes();
  }, [open]);

  useEffect(() => {
    const carregarServicos = async () => {
      if (!open) return;
      try {
        const data = await listServicos({ ativo: true });
        setServicos(data || []);
      } catch (error) {
        console.error('Erro ao carregar serviços para NFSe:', error);
      }
    };

    carregarServicos();
  }, [open]);

  useEffect(() => {
    if (open && valorTotalPadrao > 0 && !itemsPrefillDoneRef.current && !userEditedItemsRef.current) {
      // Atualizar o primeiro item quando o valor padrão mudar
      setItems(prev => {
        const newItems = [...prev];
        if (newItems.length > 0) {
          newItems[0] = {
            ...newItems[0],
            valor_unitario: valorTotalPadrao,
            valor_total: valorTotalPadrao * newItems[0].quantidade
          };
        }
        return newItems;
      });
    }
  }, [valorTotalPadrao, open]);

  // � DEBUG: Log imediato quando modal abre
  useEffect(() => {
    if (open) {
      console.log('[DialogEmitirNota] 🔍 MODAL ABRIU - Verificando props recebidas:', {
        itensPedidoComFiscal_recebido: itensPedidoComFiscal,
        itensPedidoComFiscal_type: typeof itensPedidoComFiscal,
        itensPedidoComFiscal_isArray: Array.isArray(itensPedidoComFiscal),
        itensPedidoComFiscal_length: itensPedidoComFiscal?.length || 0,
        itensPedido_recebido: itensPedido,
        descricaoPedido_recebido: descricaoPedido,
        orderCode_recebido: orderCode
      });
    }
  }, [open, itensPedidoComFiscal, itensPedido, descricaoPedido, orderCode]);

  // �📅 NOVO: Carregar items do pedido quando modal abrir
  useEffect(() => {
    if (!open) return;

    // Evitar sobrescrever o que o usuário digitou ou repetir prefill na mesma abertura
    if (itemsPrefillDoneRef.current || userEditedItemsRef.current) {
      return;
    }

    // ✨ DEBUG: Log detalhado
    console.log('[DialogEmitirNota] Tentando pre-preencher items:', {
      temPersonalizations: (itensPedido?.length || 0) > 0,
      temDescricao: !!descricaoPedido,
      temCatalogFiscal: Array.isArray(itensPedidoComFiscal) && itensPedidoComFiscal.length > 0,
      orderCode,
      itensPedidoLength: itensPedido?.length || 0,
      descricaoTrimmed: descricaoPedido?.trim().substring(0, 100) || 'vazio'
    });

    // ⚠️ NÃO FAZER NADA SE TEMOS DADOS FISCAIS - Deixar para o useEffect seguinte processar!
    if (Array.isArray(itensPedidoComFiscal) && itensPedidoComFiscal.length > 0) {
      console.log('[DialogEmitirNota] ⏭️ Pulando este useEffect - itensPedidoComFiscal será processado no próximo');
      return;
    }

    // Prioridade 1: Usar personalizations se existirem
    if (itensPedido && itensPedido.length > 0) {
      console.log('[DialogEmitirNota] ✅ Carregando items dos personalizations...');
      
      const itemsDoaPedido = itensPedido.map((itemPedido, index) => {
        const descricaoPedidoFormatada = [
          itemPedido.person_name,
          itemPedido.size ? `Tamanho: ${itemPedido.size}` : null,
          itemPedido.notes ? `Observações: ${itemPedido.notes}` : null
        ]
          .filter(Boolean)
          .join(' | ');

        return {
          numero_item: index + 1,
          codigo_produto: `${orderCode || 'PROD'}-${index + 1}`,
          descricao: descricaoPedidoFormatada || `Item ${index + 1}`,
          ncm: '', // ⚠️ Vazio - será preenchido com dados do catálogo ou manualmente
          cfop: '5102', // CFOP padrão de venda (pode ser alterado pelo usuário)
          unidade: 'UN',
          quantidade: itemPedido.quantity || 1,
          valor_unitario: 0,
          valor_total: 0,
          ...calcularIbsCbsHelper(0)
        } as NotaItem;
      });

      setItems(itemsDoaPedido);
      itemsPrefillDoneRef.current = true;
      return;
    }

    // Prioridade 2: Processar descrição do pedido se houver
    if (descricaoPedido && descricaoPedido.trim()) {
      console.log('[DialogEmitirNota] ✅ Carregando items da descrição do pedido...');
      
      // Função simplificada: apenas usar a descrição como está, sem parsing complexo
      // A descrição já vem formatada corretamente do banco de dados
      const parseOrderItems = (description: string) => {
        if (!description || !description.trim()) return [];
        
        // Simplesmente usar a descrição completa como um único item
        // porque ela já contém nome + tamanho + cor + qtd formatados
        return [{
          name: description.trim(),
          quantity: 1,
          fullText: description.trim()
        }];
      };
      
      const orderItems = parseOrderItems(descricaoPedido);
      
      if (orderItems.length > 0) {
        const itemsDoaPedido = orderItems.map((item, index) => {
          return {
            numero_item: index + 1,
            codigo_produto: `${orderCode || 'PROD'}-${index + 1}`,
            descricao: item.name, // Usa a descrição já formatada do pedido
            ncm: '', // ⚠️ Vazio - será preenchido com dados do catálogo ou manualmente
            cfop: '5102', // CFOP padrão de venda (pode ser alterado pelo usuário)
            unidade: 'UN',
            quantidade: 1,
            valor_unitario: 0,
            valor_total: 0,
            ...calcularIbsCbsHelper(0)
          } as NotaItem;
        });

        console.log('[DialogEmitirNota] ✅ Items da descrição carregados:', itemsDoaPedido.length);
        setItems(itemsDoaPedido);
        itemsPrefillDoneRef.current = true;
        return;
      }
    }

    // Se nada foi encontrado, manter o item padrão
    console.log('[DialogEmitirNota] ℹ️ Nenhum item encontrado, mantendo padrão');
  }, [open, itensPedido, descricaoPedido, orderCode, itensPedidoComFiscal]);

  // ✅ NOVO: Carregar dados fiscais dos produtos do catálogo (PRIORIDADE!)
  useEffect(() => {
    console.log('[DialogEmitirNota] 🔄 useEffect ACIONADO com itensPedidoComFiscal:', {
      open,
      itensPedidoComFiscal_type: typeof itensPedidoComFiscal,
      itensPedidoComFiscal_isArray: Array.isArray(itensPedidoComFiscal),
      itensPedidoComFiscal_length: itensPedidoComFiscal?.length || 0,
      itensPedidoComFiscal_isObject: itensPedidoComFiscal !== null && typeof itensPedidoComFiscal === 'object' && !Array.isArray(itensPedidoComFiscal),
      itensPedidoComFiscal_keys: itensPedidoComFiscal ? Object.keys(itensPedidoComFiscal) : [],
      itensPedidoComFiscal_value: itensPedidoComFiscal,
      itensPedidoComFiscal_stringified: JSON.stringify(itensPedidoComFiscal, null, 2)
    });

    // ⚠️ IMPORTANTE: Verificar se é array (não object vazio)
    const isValidArray = Array.isArray(itensPedidoComFiscal) && itensPedidoComFiscal.length > 0;
    
    if (!open || !isValidArray || itemsPrefillDoneRef.current || userEditedItemsRef.current) {
      console.log('[DialogEmitirNota] ⚠️ Condição não atendida - retornando sem processar', {
        open, 
        isValidArray,
        isProblemArray: Array.isArray(itensPedidoComFiscal) ? 'é array mas vazio' : 'NÃO é array (é ' + typeof itensPedidoComFiscal + ')'
      });
      return;
    }

    console.log('[DialogEmitirNota] ✅ Carregando dados fiscais dos produtos do catálogo (PRIORIDADE!):', {
      quantidade: itensPedidoComFiscal.length,
      produtos: itensPedidoComFiscal.map(p => ({ 
        id: p.id, 
        name: p.name, 
        fiscal_data: p.fiscal_data 
      }))
    });

    // Criar items com dados fiscais dos produtos
    const itemsComFiscal: NotaItem[] = itensPedidoComFiscal.map((produto, index) => {
      const itemFiscal = {
        numero_item: index + 1,
        codigo_produto: produto.id || `PROD-${index + 1}`,
        descricao: produto.name,
        // ✅ Usar NCM dos dados fiscais ou padrão
        ncm: produto.fiscal_data?.ncm_code || '6204.62.00',
        cfop: '5102',
        unidade: 'UN',
        quantidade: produto.quantity || 1,
        valor_unitario: produto.price || 0,
        valor_total: (produto.price || 0) * (produto.quantity || 1),
        // ✅ NOVO: Dados fiscais do produto
        cest_code: produto.fiscal_data?.cest_code,
        icms_origem: produto.fiscal_data?.icms_origem,
        icms_aliquota: produto.fiscal_data?.icms_aliquota,
        pis_aliquota: produto.fiscal_data?.pis_aliquota,
        cofins_aliquota: produto.fiscal_data?.cofins_aliquota,
        ipi_aliquota: produto.fiscal_data?.ipi_aliquota,
        // Calcular IBS/CBS
        ...calcularIbsCbsHelper((produto.price || 0) * (produto.quantity || 1))
      } as NotaItem;
      
      console.log(`[DialogEmitirNota] Item ${index + 1} com fiscal_data:`, {
        name: produto.name,
        ncm: itemFiscal.ncm,
        cest_code: itemFiscal.cest_code,
        icms_aliquota: itemFiscal.icms_aliquota,
        pis_aliquota: itemFiscal.pis_aliquota,
        cofins_aliquota: itemFiscal.cofins_aliquota,
        ipi_aliquota: itemFiscal.ipi_aliquota
      });
      
      return itemFiscal;
    });

    console.log('[DialogEmitirNota] ✅ Definindo items com dados fiscais (quantidade:', itemsComFiscal.length + ')');
    setItems(itemsComFiscal);
    itemsPrefillDoneRef.current = true;
  }, [open]); // ⚠️ REMOVIDO itensPedidoComFiscal da dependência para não resetar quando usuário edita items

  // ============================================================================
  // FUNÇÃO PARA CRIAR ITENS COM IBS/CBS CALCULADOS
  // ============================================================================

  const adicionarItem = () => {
    userEditedItemsRef.current = true;
    const novoItem: NotaItem = {
      numero_item: items.length + 1,
      codigo_produto: `${orderCode || 'PROD'}-${items.length + 1}`,
      descricao: '',
      ncm: '6204.62.00',
      cfop: '5102',
      unidade: 'UN',
      quantidade: 1,
      valor_unitario: 0,
      valor_total: 0,
      ...calcularIbsCbsHelper(0) // Inicializar com valores zerados
    };
    setItems([...items, novoItem]);
  };

  const removerItem = (index: number) => {
    userEditedItemsRef.current = true;
    if (items.length === 1) {
      toast.error('A nota deve ter pelo menos um item');
      return;
    }
    const novosItems = items.filter((_, i) => i !== index);
    // Renumerar itens
    novosItems.forEach((item, i) => {
      item.numero_item = i + 1;
    });
    setItems(novosItems);
  };

  const atualizarItem = (index: number, campo: keyof NotaItem, valor: string | number) => {
    userEditedItemsRef.current = true;
    const novosItems = [...items];
    novosItems[index] = {
      ...novosItems[index],
      [campo]: valor
    };
    
    // Recalcular valor_total quando quantidade ou valor_unitario mudar
    if (campo === 'quantidade' || campo === 'valor_unitario') {
      novosItems[index].valor_total = novosItems[index].quantidade * novosItems[index].valor_unitario;
      
      // NOVO: Calcular IBS/CBS automaticamente quando valor mudar
      const ibsCbsCalculado = calcularIbsCbsHelper(novosItems[index].valor_total);
      novosItems[index] = {
        ...novosItems[index],
        ...ibsCbsCalculado
      };
    }
    
    setItems(novosItems);
  };

  const parseDecimalInput = (value: string): number | null => {
    const normalized = value.replace(',', '.').trim();
    if (normalized === '') return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const normalizarDecimalString = (value: string) => {
    const parsed = parseDecimalInput(value);
    if (parsed === null) return '';
    return String(parsed);
  };

  const normalizarCodigoTributarioMunicipio = (value: string) => {
    const digits = String(value || '').replace(/\D/g, '');
    return digits.substring(0, 3);
  };

  const valorTotal = items.reduce((acc, item) => acc + item.valor_total, 0);

  const servicosFiltrados = servicos.filter((servico) => {
    if (!servicoSearchTerm.trim()) return true;
    const termo = servicoSearchTerm.toLowerCase();
    return (
      servico.nome.toLowerCase().includes(termo) ||
      (servico.descricao || '').toLowerCase().includes(termo) ||
      (servico.categoria || '').toLowerCase().includes(termo)
    );
  });

  const preencherNfseComServico = () => {
    const servicoSelecionado = servicos.find((s) => s.id === servicoSelecionadoId);
    if (!servicoSelecionado) {
      toast.error('Selecione um serviço para preencher os dados fiscais');
      return;
    }

    setNfseDados((prev) => ({
      ...prev,
      prestadorCodigoMunicipio: servicoSelecionado.prestador_codigo_municipio || prev.prestadorCodigoMunicipio,
      prestadorInscricaoMunicipal: servicoSelecionado.prestador_inscricao_municipal || prev.prestadorInscricaoMunicipal,
      itemListaServico: servicoSelecionado.item_lista_servico || prev.itemListaServico,
      codigoTributarioMunicipio: normalizarCodigoTributarioMunicipio(
        servicoSelecionado.codigo_tributario_municipal || prev.codigoTributarioMunicipio
      ),
      aliquota: servicoSelecionado.aliquota_nfse != null ? String(servicoSelecionado.aliquota_nfse) : prev.aliquota,
      naturezaOperacao: servicoSelecionado.natureza_operacao || prev.naturezaOperacao || '1',
      regimeEspecialTributacao: servicoSelecionado.regime_especial_tributacao || prev.regimeEspecialTributacao,
      optanteSimplesNacional: servicoSelecionado.optante_simples_nacional ?? prev.optanteSimplesNacional,
      incentivadorCultural: servicoSelecionado.incentivador_cultural ?? prev.incentivadorCultural,
      codigoMunicipioPrestacao: servicoSelecionado.codigo_municipio_prestacao || prev.codigoMunicipioPrestacao,
      discriminacao: prev.discriminacao || servicoSelecionado.discriminacao_servico || servicoSelecionado.descricao || servicoSelecionado.nome,
    }));

    toast.success('Dados fiscais do serviço aplicados');
  };

  const handleEmitir = async () => {
    // Validações de itens
    if (items.length === 0) {
      toast.error('Adicione pelo menos um item');
      return;
    }

    for (const item of items) {
      if (!item.descricao.trim()) {
        toast.error(`Item ${item.numero_item}: Descrição é obrigatória`);
        return;
      }
      if (item.quantidade <= 0) {
        toast.error(`Item ${item.numero_item}: Quantidade deve ser maior que zero`);
        return;
      }
      if (item.valor_unitario <= 0) {
        toast.error(`Item ${item.numero_item}: Valor unitário deve ser maior que zero`);
        return;
      }
      if (tipoNota !== 'NFSe' && (!item.ncm || item.ncm.length < 8)) {
        toast.error(`Item ${item.numero_item}: NCM deve ter 8 dígitos`);
        return;
      }
    }

    let nfsePayload:
      | {
          prestadorCodigoMunicipio: string;
          prestadorInscricaoMunicipal?: string;
          tomadorCodigoMunicipio: string;
          codigoMunicipioPrestacao?: string;
          itemListaServico: string;
          codigoTributarioMunicipio?: string;
          aliquota: number;
          issRetido: boolean;
          discriminacao: string;
          naturezaOperacao?: number;
          regimeEspecialTributacao?: number;
          optanteSimplesNacional?: boolean;
          incentivadorCultural?: boolean;
        }
      | undefined;

    if (tipoNota === 'NFSe') {
      if (!nfseDados.prestadorCodigoMunicipio.trim()) {
        toast.error('Município IBGE do prestador é obrigatório para NFS-e');
        return;
      }
      if (!nfseDados.tomadorCodigoMunicipio.trim()) {
        toast.error('Município IBGE do tomador é obrigatório para NFS-e');
        return;
      }
      if (!nfseDados.itemListaServico.trim()) {
        toast.error('Item da lista de serviço é obrigatório para NFS-e');
        return;
      }
      if (!nfseDados.aliquota.trim() || Number.isNaN(Number(nfseDados.aliquota))) {
        toast.error('Alíquota do ISS inválida para NFS-e');
        return;
      }

      const discriminacaoNFSe =
        nfseDados.discriminacao.trim() ||
        items.map((item) => item.descricao).join(' | ') ||
        `Serviço referente ao pedido ${orderCode}`;

      nfsePayload = {
        prestadorCodigoMunicipio: nfseDados.prestadorCodigoMunicipio.trim(),
        prestadorInscricaoMunicipal: nfseDados.prestadorInscricaoMunicipal.trim() || undefined,
        tomadorCodigoMunicipio: nfseDados.tomadorCodigoMunicipio.trim(),
        codigoMunicipioPrestacao:
          nfseDados.codigoMunicipioPrestacao.trim() || nfseDados.tomadorCodigoMunicipio.trim(),
        itemListaServico: nfseDados.itemListaServico.trim(),
        codigoTributarioMunicipio:
          normalizarCodigoTributarioMunicipio(nfseDados.codigoTributarioMunicipio.trim()) || undefined,
        aliquota: Number(nfseDados.aliquota),
        issRetido: nfseDados.issRetido,
        discriminacao: discriminacaoNFSe,
        naturezaOperacao: Number(nfseDados.naturezaOperacao || '1'),
        regimeEspecialTributacao: nfseDados.regimeEspecialTributacao.trim()
          ? Number(nfseDados.regimeEspecialTributacao)
          : undefined,
        optanteSimplesNacional: nfseDados.optanteSimplesNacional,
        incentivadorCultural: nfseDados.incentivadorCultural,
      };
    }

    // Validações do cliente (CPF/CNPJ é importante para nota fiscal válida)
    if (!cliente.nome.trim()) {
      toast.error('Nome do cliente é obrigatório');
      return;
    }

    // CPF/CNPJ não é obrigatório em homologação, mas recomendado
    if (cliente.cpf_cnpj) {
      const cpfCnpjLimpo = cliente.cpf_cnpj.replace(/\D/g, '');
      if (cpfCnpjLimpo.length !== 11 && cpfCnpjLimpo.length !== 14) {
        toast.error('CPF deve ter 11 dígitos ou CNPJ deve ter 14 dígitos');
        return;
      }
    }

    // Validação IE para CNPJ
    const cpfCnpjLimpo = cliente.cpf_cnpj?.replace(/\D/g, '') || '';
    if (cpfCnpjLimpo.length === 14) {
      if (!cliente.ie_isento && (!cliente.inscricao_estadual || !cliente.inscricao_estadual.trim())) {
        toast.error('Para CNPJ, informe a Inscrição Estadual ou marque como ISENTO.');
        return;
      }
    }

    // Antes de emitir, atualizar IE/ISENTO no Supabase se clienteId e for CNPJ
    const cpfCnpjLimpo2 = cliente.cpf_cnpj?.replace(/\D/g, '') || '';
    if (clienteId && cpfCnpjLimpo2.length === 14) {
      await updateCustomer(clienteId, {
        inscricao_estadual: cliente.ie_isento ? null : (cliente.inscricao_estadual || null),
        ie_isento: !!cliente.ie_isento
      });
    }
    // Se não informado no modal, buscar do banco
    if (clienteId && cpfCnpjLimpo2.length === 14 && (!cliente.inscricao_estadual && !cliente.ie_isento)) {
      const { data } = await supabase.from('customers').select('inscricao_estadual,ie_isento').eq('id', clienteId).single();
      if (data) {
        cliente.inscricao_estadual = data.inscricao_estadual || '';
        cliente.ie_isento = !!data.ie_isento;
      }
    }
    await onEmitir(items, tipoNota, cliente, nfsePayload);
  };

  const formatarCPFCNPJ = (valor: string) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 11) {
      // CPF: 000.000.000-00
      if (numeros.length <= 3) return numeros;
      if (numeros.length <= 6) return numeros.replace(/(\d{3})(\d+)/, '$1.$2');
      if (numeros.length <= 9) return numeros.replace(/(\d{3})(\d{3})(\d+)/, '$1.$2.$3');
      return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else {
      // CNPJ: 00.000.000/0000-00
      if (numeros.length <= 2) return numeros;
      if (numeros.length <= 5) return numeros.replace(/(\d{2})(\d+)/, '$1.$2');
      if (numeros.length <= 8) return numeros.replace(/(\d{2})(\d{3})(\d+)/, '$1.$2.$3');
      if (numeros.length <= 12) return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d+)/, '$1.$2.$3/$4');
      return numeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
  };

  const formatarCEP = (valor: string) => {
    const numeros = valor.replace(/\D/g, '').substring(0, 8);
    return numeros.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const aplicarClienteSelecionado = (clienteSelecionado: ClienteOption) => {
    setClienteId(clienteSelecionado.id);
    setCliente({
      nome: clienteSelecionado.name || '',
      cpf_cnpj: clienteSelecionado.cpf_cnpj ? formatarCPFCNPJ(clienteSelecionado.cpf_cnpj) : '',
      email: clienteSelecionado.email || '',
      telefone: clienteSelecionado.phone || '',
      inscricao_estadual: clienteSelecionado.inscricao_estadual || '',
      ie_isento: !!clienteSelecionado.ie_isento,
      endereco: {
        logradouro: clienteSelecionado.endereco_logradouro || '',
        numero: clienteSelecionado.endereco_numero || '',
        complemento: clienteSelecionado.endereco_complemento || '',
        bairro: clienteSelecionado.endereco_bairro || '',
        cidade: clienteSelecionado.endereco_cidade || '',
        uf: clienteSelecionado.endereco_uf || '',
        cep: clienteSelecionado.endereco_cep || '',
      }
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[96vw] max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Emitir Nota Fiscal</DialogTitle>
          <DialogDescription>
            Configure os itens da nota fiscal antes de emitir
          </DialogDescription>
        </DialogHeader>

  <div className="space-y-6 w-full max-w-4xl mx-auto">
          {/* Dados do Cliente */}
          <div className="space-y-4 border rounded-lg p-4 bg-slate-50">
            <div>
              <h3 className="text-sm font-semibold mb-3">Dados do Cliente (Destinatário)</h3>
              <p className="text-xs text-muted-foreground mb-4">
                Estes dados aparecerão na nota fiscal. CPF/CNPJ é recomendado para notas válidas.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="cliente-nome">Nome/Razão Social *</Label>
                <div className="mb-2 flex flex-col gap-2">
                  <Popover open={clientePopoverOpen} onOpenChange={setClientePopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={clientePopoverOpen}
                        className="w-full justify-between"
                        disabled={isLoadingClientes}
                      >
                        {clienteSelecionadoId
                          ? clientesDisponiveis.find((item) => item.id === clienteSelecionadoId)?.name || "Selecionar cliente"
                          : isLoadingClientes
                          ? "Carregando clientes..."
                          : "Selecionar cliente (opcional)"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] p-0" align="start">
                      <Command>
                        <CommandInput
                          placeholder="Buscar cliente..."
                          value={clienteSearchTerm}
                          onValueChange={setClienteSearchTerm}
                        />
                        <CommandList>
                          <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
                          <CommandGroup>
                            {clientesDisponiveis
                              .filter((clienteItem) =>
                                clienteItem.name.toLowerCase().includes(clienteSearchTerm.toLowerCase())
                              )
                              .map((clienteItem) => (
                                <CommandItem
                                  key={clienteItem.id}
                                  value={clienteItem.name}
                                  onSelect={() => {
                                    setClienteSelecionadoId(clienteItem.id);
                                    setClientePopoverOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      clienteSelecionadoId === clienteItem.id ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {clienteItem.name}
                                </CommandItem>
                              ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button
                    type="button"
                    className="w-[20%] min-w-[180px] self-start bg-[#6d28d9] text-white hover:bg-[#5b21b6] px-3 text-center justify-center whitespace-nowrap"
                    onClick={() => {
                      const clienteSelecionado = clientesDisponiveis.find((item) => item.id === clienteSelecionadoId);
                      if (!clienteSelecionado) {
                        toast.error("Selecione um cliente para preencher automaticamente");
                        return;
                      }
                      aplicarClienteSelecionado(clienteSelecionado);
                    }}
                  >
                    Preencher automático
                  </Button>
                </div>
                <Input
                  id="cliente-nome"
                  value={cliente.nome}
                  onChange={(e) => setCliente({ ...cliente, nome: e.target.value })}
                  placeholder="Nome completo ou razão social"
                  required
                />
              </div>

              {/* Inscrição Estadual e ISENTO - só para CNPJ */}
              {cliente.cpf_cnpj?.replace(/\D/g, '').length === 14 && (
                <div className="md:col-span-2 flex gap-4 items-end">
                  <div className="flex-1">
                    <Label htmlFor="cliente-ie">Inscrição Estadual (IE)</Label>
                    <Input
                      id="cliente-ie"
                      value={cliente.ie_isento ? 'ISENTO' : (cliente.inscricao_estadual || '')}
                      onChange={e => setCliente({ ...cliente, inscricao_estadual: e.target.value })}
                      placeholder="Inscrição Estadual"
                      disabled={cliente.ie_isento}
                    />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="cliente-ie-isento"
                      checked={!!cliente.ie_isento}
                      onChange={e => setCliente({ ...cliente, ie_isento: e.target.checked, inscricao_estadual: e.target.checked ? '' : cliente.inscricao_estadual })}
                    />
                    <Label htmlFor="cliente-ie-isento">ISENTO</Label>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="cliente-cpf-cnpj">CPF/CNPJ</Label>
                <Input
                  id="cliente-cpf-cnpj"
                  value={cliente.cpf_cnpj}
                  onChange={(e) => {
                    const valor = e.target.value.replace(/\D/g, '');
                    if (valor === '') {
                      setCliente({ ...cliente, cpf_cnpj: '' });
                    } else {
                      setCliente({ ...cliente, cpf_cnpj: formatarCPFCNPJ(valor) });
                    }
                  }}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  maxLength={18}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {cliente.cpf_cnpj?.replace(/\D/g, '').length === 11 ? 'CPF' : 
                   cliente.cpf_cnpj?.replace(/\D/g, '').length === 14 ? 'CNPJ' : 
                   'Digite CPF (11 dígitos) ou CNPJ (14 dígitos)'}
                </p>
              </div>

              <div>
                <Label htmlFor="cliente-email">Email</Label>
                <Input
                  id="cliente-email"
                  type="email"
                  value={cliente.email || ''}
                  onChange={(e) => setCliente({ ...cliente, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  className="bg-background text-foreground"
                />
              </div>

              <div>
                <Label htmlFor="cliente-telefone">Telefone</Label>
                <Input
                  id="cliente-telefone"
                  value={cliente.telefone || ''}
                  onChange={(e) => {
                    const valor = e.target.value.replace(/\D/g, '').substring(0, 11);
                    setCliente({ ...cliente, telefone: valor });
                  }}
                  placeholder="(00) 00000-0000"
                />
              </div>

              {/* Endereço */}
              <div className="md:col-span-2 border-t pt-4 mt-2">
                <h4 className="text-sm font-medium mb-3">Endereço</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="cliente-logradouro">Logradouro (Rua, Avenida, etc.)</Label>
                    <Input
                      id="cliente-logradouro"
                      value={cliente.endereco?.logradouro || ''}
                      onChange={(e) => setCliente({
                        ...cliente,
                        endereco: { ...cliente.endereco, logradouro: e.target.value }
                      })}
                      placeholder="Rua, Avenida, etc."
                    />
                  </div>

                  <div>
                    <Label htmlFor="cliente-numero">Número</Label>
                    <Input
                      id="cliente-numero"
                      value={cliente.endereco?.numero || ''}
                      onChange={(e) => setCliente({
                        ...cliente,
                        endereco: { ...cliente.endereco, numero: e.target.value }
                      })}
                      placeholder="123"
                    />
                  </div>

                  <div>
                    <Label htmlFor="cliente-complemento">Complemento</Label>
                    <Input
                      id="cliente-complemento"
                      value={cliente.endereco?.complemento || ''}
                      onChange={(e) => setCliente({
                        ...cliente,
                        endereco: { ...cliente.endereco, complemento: e.target.value }
                      })}
                      placeholder="Apto, Bloco, etc."
                    />
                  </div>

                  <div>
                    <Label htmlFor="cliente-bairro">Bairro</Label>
                    <Input
                      id="cliente-bairro"
                      value={cliente.endereco?.bairro || ''}
                      onChange={(e) => setCliente({
                        ...cliente,
                        endereco: { ...cliente.endereco, bairro: e.target.value }
                      })}
                      placeholder="Bairro"
                    />
                  </div>

                  <div>
                    <Label htmlFor="cliente-cidade">Cidade</Label>
                    <Input
                      id="cliente-cidade"
                      value={cliente.endereco?.cidade || ''}
                      onChange={(e) => setCliente({
                        ...cliente,
                        endereco: { ...cliente.endereco, cidade: e.target.value }
                      })}
                      placeholder="Cidade"
                    />
                  </div>

                  <div>
                    <Label htmlFor="cliente-uf">UF</Label>
                    <Input
                      id="cliente-uf"
                      value={cliente.endereco?.uf || ''}
                      onChange={(e) => {
                        const uf = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').substring(0, 2);
                        setCliente({
                          ...cliente,
                          endereco: { ...cliente.endereco, uf }
                        });
                      }}
                      placeholder="MG"
                      maxLength={2}
                    />
                  </div>

                  <div>
                    <Label htmlFor="cliente-cep">CEP</Label>
                    <Input
                      id="cliente-cep"
                      value={cliente.endereco?.cep || ''}
                      onChange={(e) => {
                        const valor = e.target.value.replace(/\D/g, '').substring(0, 8);
                        setCliente({
                          ...cliente,
                          endereco: { ...cliente.endereco, cep: formatarCEP(valor) }
                        });
                      }}
                      placeholder="00000-000"
                      maxLength={9}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tipo de Nota */}
          <div className="space-y-2">
            <Label htmlFor="tipoNota">Tipo de Nota *</Label>
            <Select value={tipoNota} onValueChange={(value: 'NFe' | 'NFCe' | 'NFSe') => setTipoNota(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NFe">NFe - Nota Fiscal Eletrônica (Produtos)</SelectItem>
                <SelectItem value="NFCe">NFCe - Nota Fiscal ao Consumidor Eletrônica</SelectItem>
                <SelectItem value="NFSe">NFS-e - Nota Fiscal de Serviço Eletrônica</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {tipoNota === 'NFe' && 'Para vendas de produtos com validade fiscal'}
              {tipoNota === 'NFCe' && 'Para vendas ao consumidor final (rápida)'}
              {tipoNota === 'NFSe' && 'Para emissão de serviços (com dados municipais de NFS-e)'}
            </p>
          </div>

          {tipoNota === 'NFSe' && (
            <div className="space-y-4 border rounded-lg p-4 bg-blue-50">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h3 className="text-sm font-semibold">Dados Fiscais da NFS-e</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Selecione um serviço para preencher automaticamente os dados fiscais cadastrados.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Serviço (opcional para auto preenchimento)</Label>
                  <div className="flex gap-2 mt-2">
                    <Popover open={servicoPopoverOpen} onOpenChange={setServicoPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" role="combobox" className="w-full justify-between">
                          {servicoSelecionadoId
                            ? servicos.find((s) => s.id === servicoSelecionadoId)?.nome || 'Selecionar serviço'
                            : 'Selecionar serviço'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[380px] p-0" align="start">
                        <Command>
                          <CommandInput
                            placeholder="Buscar serviço..."
                            value={servicoSearchTerm}
                            onValueChange={setServicoSearchTerm}
                          />
                          <CommandList>
                            <CommandEmpty>Nenhum serviço encontrado.</CommandEmpty>
                            <CommandGroup>
                              {servicosFiltrados.map((servico) => (
                                <CommandItem
                                  key={servico.id}
                                  value={`${servico.nome} ${servico.descricao || ''} ${servico.categoria || ''}`}
                                  onSelect={() => {
                                    setServicoSelecionadoId(servico.id);
                                    setServicoPopoverOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      servicoSelecionadoId === servico.id ? 'opacity-100' : 'opacity-0'
                                    )}
                                  />
                                  {servico.nome}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <Button type="button" variant="outline" onClick={preencherNfseComServico}>
                      Preencher dados fiscais
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="nfse-prestador-municipio">Município IBGE do Prestador *</Label>
                  <Input
                    id="nfse-prestador-municipio"
                    value={nfseDados.prestadorCodigoMunicipio}
                    onChange={(e) => setNfseDados({ ...nfseDados, prestadorCodigoMunicipio: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="nfse-prestador-im">Inscrição Municipal Prestador</Label>
                  <Input
                    id="nfse-prestador-im"
                    value={nfseDados.prestadorInscricaoMunicipal}
                    onChange={(e) => setNfseDados({ ...nfseDados, prestadorInscricaoMunicipal: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="nfse-tomador-municipio">Município IBGE do Tomador *</Label>
                  <Input
                    id="nfse-tomador-municipio"
                    value={nfseDados.tomadorCodigoMunicipio}
                    onChange={(e) => setNfseDados({ ...nfseDados, tomadorCodigoMunicipio: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="nfse-prestacao-municipio">Município IBGE da Prestação</Label>
                  <Input
                    id="nfse-prestacao-municipio"
                    value={nfseDados.codigoMunicipioPrestacao}
                    onChange={(e) => setNfseDados({ ...nfseDados, codigoMunicipioPrestacao: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="nfse-item-lista">Item Lista Serviço *</Label>
                  <Input
                    id="nfse-item-lista"
                    value={nfseDados.itemListaServico}
                    onChange={(e) => setNfseDados({ ...nfseDados, itemListaServico: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="nfse-cod-trib">Código Tributário Municipal</Label>
                  <Input
                    id="nfse-cod-trib"
                    value={nfseDados.codigoTributarioMunicipio}
                    onChange={(e) =>
                      setNfseDados({
                        ...nfseDados,
                        codigoTributarioMunicipio: normalizarCodigoTributarioMunicipio(e.target.value)
                      })
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="nfse-aliquota">Alíquota ISS (%) *</Label>
                  <Input
                    id="nfse-aliquota"
                    type="number"
                    step="0.01"
                    value={nfseDados.aliquota}
                    onChange={(e) => setNfseDados({ ...nfseDados, aliquota: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="nfse-natureza">Natureza da Operação</Label>
                  <Input
                    id="nfse-natureza"
                    value={nfseDados.naturezaOperacao}
                    onChange={(e) => setNfseDados({ ...nfseDados, naturezaOperacao: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="nfse-regime">Regime Especial Tributação</Label>
                  <Input
                    id="nfse-regime"
                    value={nfseDados.regimeEspecialTributacao}
                    onChange={(e) => setNfseDados({ ...nfseDados, regimeEspecialTributacao: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="nfse-iss-retido">ISS Retido</Label>
                  <Select
                    value={nfseDados.issRetido ? 'true' : 'false'}
                    onValueChange={(v) => setNfseDados({ ...nfseDados, issRetido: v === 'true' })}
                  >
                    <SelectTrigger id="nfse-iss-retido">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">Não</SelectItem>
                      <SelectItem value="true">Sim</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="nfse-simples">Optante Simples Nacional</Label>
                  <Select
                    value={nfseDados.optanteSimplesNacional ? 'true' : 'false'}
                    onValueChange={(v) => setNfseDados({ ...nfseDados, optanteSimplesNacional: v === 'true' })}
                  >
                    <SelectTrigger id="nfse-simples">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Sim</SelectItem>
                      <SelectItem value="false">Não</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="nfse-incentivo">Incentivador Cultural</Label>
                  <Select
                    value={nfseDados.incentivadorCultural ? 'true' : 'false'}
                    onValueChange={(v) => setNfseDados({ ...nfseDados, incentivadorCultural: v === 'true' })}
                  >
                    <SelectTrigger id="nfse-incentivo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">Não</SelectItem>
                      <SelectItem value="true">Sim</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="nfse-discriminacao">Discriminação do Serviço *</Label>
                  <Textarea
                    id="nfse-discriminacao"
                    value={nfseDados.discriminacao}
                    onChange={(e) => setNfseDados({ ...nfseDados, discriminacao: e.target.value })}
                    placeholder="Descrição do serviço prestado"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Itens da Nota Fiscal - Layout em Cards */}
          <div className="space-y-4 border rounded-lg p-4 bg-slate-50">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Itens da Nota Fiscal</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Preencha os detalhes de cada item que será incluído na nota
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={adicionarItem}>
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Item
              </Button>
            </div>

            {/* Lista de Items em Cards */}
            <div className="space-y-4">
              {items.map((item, index) => (
                <div key={index} className="border rounded-lg p-4 bg-white space-y-4 hover:shadow-md transition-shadow">
                  {/* Header do Item */}
                  <div className="flex items-center justify-between border-b pb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold bg-purple-100 text-purple-700 px-3 py-1 rounded-full">
                        Item {item.numero_item}
                      </span>
                      <span className="text-xs text-muted-foreground">Código: {item.codigo_produto}</span>
                    </div>
                    {items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => removerItem(index)}
                        title="Remover item"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>

                  {/* Descrição - Destaque principal */}
                  <div>
                    <Label htmlFor={`item-descricao-${index}`} className="text-sm font-semibold">
                      Descrição do Produto *
                    </Label>
                    <Input
                      id={`item-descricao-${index}`}
                      value={item.descricao}
                      onChange={(e) => atualizarItem(index, 'descricao', e.target.value)}
                      placeholder="Descrição completa do produto"
                      className="mt-2 font-medium text-base border-purple-200 focus:border-purple-500"
                      required
                    />
                  </div>

                  {/* Grid de Campos */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Código */}
                    <div>
                      <Label htmlFor={`item-codigo-${index}`} className="text-xs font-semibold">
                        Código
                      </Label>
                      <Input
                        id={`item-codigo-${index}`}
                        value={item.codigo_produto}
                        onChange={(e) => atualizarItem(index, 'codigo_produto', e.target.value)}
                        placeholder="Código"
                        className="mt-2 text-sm"
                      />
                    </div>

                    {/* NCM */}
                    <div>
                      <Label htmlFor={`item-ncm-${index}`} className="text-xs font-semibold">
                        NCM {tipoNota !== 'NFSe' ? '*' : '(não obrigatório para NFS-e)'}
                      </Label>
                      <Input
                        id={`item-ncm-${index}`}
                        value={tipoNota === 'NFSe' ? (item.ncm || '00000000') : item.ncm}
                        onChange={(e) => {
                          if (tipoNota === 'NFSe') return;
                          const valor = e.target.value.replace(/\D/g, '').substring(0, 8);
                          atualizarItem(index, 'ncm', valor);
                        }}
                        placeholder="00000000"
                        maxLength={8}
                        className="mt-2 text-sm"
                        required={tipoNota !== 'NFSe'}
                        disabled={tipoNota === 'NFSe'}
                      />
                    </div>

                    {/* CFOP */}
                    <div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`item-cfop-${index}`} className="text-xs font-semibold">
                            CFOP
                          </Label>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-3 h-3 text-blue-500 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs max-w-xs">
                                <p className="font-semibold mb-1">Código Fiscal de Operação</p>
                                <p>{getDescricaoCFOP(item.cfop)}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                        <Select
                          value={item.cfop}
                          onValueChange={(value) => {
                            if (isValidarCFOP(value)) {
                              atualizarItem(index, 'cfop', value);
                            }
                          }}
                        >
                          <SelectTrigger id={`item-cfop-${index}`} className="mt-2 text-sm h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {CFOPsMaisComuns.map((cfop) => (
                              <SelectItem key={cfop.codigo} value={cfop.codigo}>
                                {cfop.codigo} - {cfop.categoria.split(' - ')[1]}
                              </SelectItem>
                            ))}
                            <div className="px-2 py-1.5 text-xs text-gray-500 border-t">
                              Selecione o CFOP da operação realizada
                            </div>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* CST (Situação Tributária ICMS) */}
                      <div>
                        <Label htmlFor={`item-cst-${index}`} className="text-xs font-semibold">
                          CST *
                        </Label>
                        <Select
                          value={item.icms_situacao_tributaria || '00'}
                          onValueChange={(value) => {
                            if (isValidarCST(value)) {
                              atualizarItem(index, 'icms_situacao_tributaria', value);
                            }
                          }}
                        >
                          <SelectTrigger id={`item-cst-${index}`} className="mt-2 text-sm h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {CSTsMaisComuns.map((cst) => (
                              <SelectItem key={cst.codigo} value={cst.codigo}>
                                {cst.codigo} - {cst.descricao.substring(0, 40)}
                              </SelectItem>
                            ))}
                            <div className="px-2 py-1.5 text-xs text-gray-500 border-t">
                              Selecione a situação tributária ICMS
                            </div>
                          </SelectContent>
                        </Select>
                        {item.icms_situacao_tributaria && (
                          <p className="text-xs text-gray-500 mt-1">
                            {getDescricaoCST(item.icms_situacao_tributaria)}
                          </p>
                        )}
                      </div>

                      {/* IPI Situação Tributária */}
                      <div>
                        <Label htmlFor={`item-ipi-situacao-${index}`} className="text-xs font-semibold">
                          IPI - Situação
                        </Label>
                        <Select
                          value={item.ipi_situacao_tributaria || '01'}
                          onValueChange={(value) => {
                            if (isValidarIPISituacao(value)) {
                              atualizarItem(index, 'ipi_situacao_tributaria', value);
                            }
                          }}
                        >
                          <SelectTrigger id={`item-ipi-situacao-${index}`} className="mt-2 text-sm h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-64">
                            {IPISituacoesMaisComuns.map((situacao) => (
                              <SelectItem key={situacao.codigo} value={situacao.codigo}>
                                {situacao.codigo} - {situacao.descricao}
                              </SelectItem>
                            ))}
                            <div className="px-2 py-1.5 text-xs text-gray-500 border-t">
                              Consulte seu contador
                            </div>
                          </SelectContent>
                        </Select>
                        {item.ipi_situacao_tributaria && (
                          <p className="text-xs text-gray-500 mt-1">
                            {getDescricaoIPISituacao(item.ipi_situacao_tributaria)}
                          </p>
                        )}
                      </div>

                      {/* IPI Alíquota */}
                      <div>
                        <Label htmlFor={`item-ipi-aliquota-${index}`} className="text-xs font-semibold">
                          IPI Alíquota (%)
                        </Label>
                        <Input
                          id={`item-ipi-aliquota-${index}`}
                          type="text"
                          inputMode="decimal"
                          value={item.ipi_aliquota ?? ''}
                          onChange={(e) => {
                            const parsed = parseDecimalInput(e.target.value);
                            if (parsed === null && e.target.value !== '') return;
                            atualizarItem(index, 'ipi_aliquota', parsed ?? 0);
                          }}
                          placeholder="Ex: 5, 10, 15"
                          className="mt-2 text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Consulte seu contador ou a TIPI (Receita Federal)
                        </p>
                      </div>
                    </div>

                    {/* Unidade */}
                    <div>
                      <Label htmlFor={`item-unidade-${index}`} className="text-xs font-semibold">
                        Unidade
                      </Label>
                      <Select
                        value={item.unidade}
                        onValueChange={(value) => atualizarItem(index, 'unidade', value)}
                      >
                        <SelectTrigger id={`item-unidade-${index}`} className="mt-2 text-sm h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UN">UN</SelectItem>
                          <SelectItem value="KG">KG</SelectItem>
                          <SelectItem value="M">M</SelectItem>
                          <SelectItem value="M2">M²</SelectItem>
                          <SelectItem value="PC">PC</SelectItem>
                          <SelectItem value="CX">CX</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Quantidade */}
                    <div>
                      <Label htmlFor={`item-qtd-${index}`} className="text-xs font-semibold">
                        Quantidade *
                      </Label>
                      <Input
                        id={`item-qtd-${index}`}
                        type="text"
                        inputMode="decimal"
                        value={item.quantidade}
                        onChange={(e) => {
                          const parsed = parseDecimalInput(e.target.value);
                          if (parsed === null) return;
                          atualizarItem(index, 'quantidade', parsed);
                        }}
                        className="mt-2 text-sm"
                        required
                      />
                    </div>

                    {/* Valor Unitário */}
                    <div>
                      <Label htmlFor={`item-valor-unit-${index}`} className="text-xs font-semibold">
                        Valor Unitário *
                      </Label>
                      <Input
                        id={`item-valor-unit-${index}`}
                        type="text"
                        inputMode="decimal"
                        value={valorUnitarioDrafts[index] ?? String(item.valor_unitario ?? '')}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setValorUnitarioDrafts((prev) => ({ ...prev, [index]: raw }));

                          const parsed = parseDecimalInput(raw);
                          if (parsed === null) return;
                          atualizarItem(index, 'valor_unitario', parsed);
                        }}
                        onBlur={() => {
                          const raw = valorUnitarioDrafts[index];
                          if (raw === undefined) return;

                          const normalized = normalizarDecimalString(raw);
                          setValorUnitarioDrafts((prev) => ({ ...prev, [index]: normalized }));

                          const parsed = parseDecimalInput(raw);
                          if (parsed !== null) {
                            atualizarItem(index, 'valor_unitario', parsed);
                          }
                        }}
                        className="mt-2 text-sm"
                        required
                      />
                    </div>

                    {/* Valor Total */}
                    <div>
                      <Label className="text-xs font-semibold">Valor Total</Label>
                      <div className="mt-2 p-3 bg-purple-50 rounded border border-purple-200 text-center">
                        <p className="text-sm font-bold text-purple-700">
                          {formatCurrency({ value: item.valor_total, currency: 'BRL' })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ✅ NOVO: SEÇÃO DE DADOS FISCAIS DOS PRODUTOS */}
            {itensPedidoComFiscal && itensPedidoComFiscal.length > 0 && items.length > 0 && (
              <div className="space-y-4 border rounded-lg p-4 bg-blue-50 border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-blue-900">
                    📋 Dados Fiscais dos Produtos do Catálogo
                  </span>
                </div>

                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div key={index} className="border rounded-lg p-3 bg-white border-blue-100">
                      <h4 className="text-xs font-bold text-blue-900 mb-2">
                        Item {item.numero_item}: {item.descricao}
                      </h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                        {/* NCM */}
                        {item.ncm && (
                          <div>
                            <p className="text-muted-foreground font-semibold">NCM</p>
                            <p className="font-mono font-bold text-gray-900">{item.ncm}</p>
                          </div>
                        )}

                        {/* CEST */}
                        {item.cest_code && (
                          <div>
                            <p className="text-muted-foreground font-semibold">CEST</p>
                            <p className="font-mono font-bold text-gray-900">{item.cest_code}</p>
                          </div>
                        )}

                        {/* ICMS Origem */}
                        {item.icms_origem !== undefined && (
                          <div>
                            <p className="text-muted-foreground font-semibold">ICMS Origem</p>
                            <p className="font-mono font-bold text-gray-900">
                              {item.icms_origem === '0' ? 'Nacional' : item.icms_origem === '1' ? 'Estrangeira' : item.icms_origem}
                            </p>
                          </div>
                        )}

                        {/* ICMS Alíquota */}
                        {item.icms_aliquota !== undefined && item.icms_aliquota > 0 && (
                          <div>
                            <p className="text-muted-foreground font-semibold">ICMS Alíq.</p>
                            <p className="font-mono font-bold text-gray-900">{item.icms_aliquota}%</p>
                          </div>
                        )}

                        {/* PIS Alíquota */}
                        {item.pis_aliquota !== undefined && item.pis_aliquota > 0 && (
                          <div>
                            <p className="text-muted-foreground font-semibold">PIS Alíq.</p>
                            <p className="font-mono font-bold text-gray-900">{item.pis_aliquota}%</p>
                          </div>
                        )}

                        {/* COFINS Alíquota */}
                        {item.cofins_aliquota !== undefined && item.cofins_aliquota > 0 && (
                          <div>
                            <p className="text-muted-foreground font-semibold">COFINS Alíq.</p>
                            <p className="font-mono font-bold text-gray-900">{item.cofins_aliquota}%</p>
                          </div>
                        )}

                        {/* IPI Alíquota */}
                        {item.ipi_aliquota !== undefined && item.ipi_aliquota > 0 && (
                          <div>
                            <p className="text-muted-foreground font-semibold">IPI Alíq.</p>
                            <p className="font-mono font-bold text-gray-900">{item.ipi_aliquota}%</p>
                          </div>
                        )}
                      </div>

                      <p className="text-xs text-blue-700 mt-2 pt-2 border-t border-blue-100">
                        💡 Estes dados foram carregados automaticamente do produto no catálogo. Você pode editá-los acima se necessário.
                      </p>
                    </div>
                  ))}
                </div>

                <div className="p-3 bg-blue-100 border border-blue-200 rounded">
                  <p className="text-xs text-blue-800">
                    <strong>✅ Automático:</strong> Os dados fiscais (NCM, CEST, alíquotas) foram importados do produto no catálogo. 
                    Qualquer alteração deve ser feita nos campos acima de cada item.
                  </p>
                </div>
              </div>
            )}

            {/* 📅 SEÇÃO DE TRIBUTAÇÃO - REFORMA 2026 */}
            <div className="space-y-4 border rounded-lg p-4 bg-amber-50 border-amber-200">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-amber-900">
                  ℹ️ Informações Tributárias (Reforma 2026 - A partir de 01/04)
                </span>
              </div>

              {/* Informação sobre automação */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-xs text-blue-800">
                  <strong>✅ Automático:</strong> Os valores de IBS (0,1% estadual), CBS (0,9% federal) e IPI são calculados automaticamente com base no valor de cada item. Você não precisa preencher valores aqui.
                </p>
              </div>

              {/* Alíquotas 2026 - Informativos */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 bg-white rounded border border-amber-100">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground font-semibold">CBS (Federal)</p>
                  <p className="text-2xl font-bold text-blue-600">0,9%</p>
                  <p className="text-xs text-muted-foreground">Fixo em 2026</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground font-semibold">IBS Estadual</p>
                  <p className="text-2xl font-bold text-green-600">0,1%</p>
                  <p className="text-xs text-muted-foreground">Maioria dos UFs</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground font-semibold">IPI (Novo)</p>
                  <p className="text-2xl font-bold text-purple-600">0,0%*</p>
                  <p className="text-xs text-muted-foreground">*Por produto</p>
                </div>
              </div>

              {/* Campos de Configuração */}
              <div className="space-y-3">
                {/* Situação Tributária */}
                <div>
                  <Label className="text-xs font-semibold">Situação Tributária IBS/CBS</Label>
                  <Select 
                    defaultValue="00"
                    onValueChange={(value) => {
                      // Atualizar situação tributária em todos os itens
                      items.forEach((_, idx) => {
                        atualizarItem(idx, 'ibs_cbs_situacao_tributaria', value);
                      });
                    }}
                  >
                    <SelectTrigger className="mt-2 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="00">00 - Operação Normal</SelectItem>
                      <SelectItem value="20">20 - Isenção</SelectItem>
                      <SelectItem value="30">30 - Suspensão</SelectItem>
                      <SelectItem value="40">40 - Diferimento</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Selecione a situação tributária padrão para todos os itens. 
                    Para a maioria dos casos, use <strong>00 (Normal)</strong>
                  </p>
                </div>

                {/* Classificação Tributária */}
                <div>
                  <Label className="text-xs font-semibold">Classificação Tributária (7 dígitos)</Label>
                  <Input
                    maxLength={7}
                    defaultValue="0000000"
                    placeholder="Ex: 0000000 ou deixe em branco"
                    className="mt-2 text-sm"
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '').substring(0, 7);
                      items.forEach((_, idx) => {
                        atualizarItem(idx, 'ibs_cbs_classificacao_tributaria', value || '0000000');
                      });
                    }}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Deixe como <strong>0000000</strong> se não souber a classificação específica. 
                    Será atualizado quando a Focus NF disponibilizar tabela de classificações.
                  </p>
                </div>
              </div>

              {/* Aviso importante */}
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-xs text-yellow-800 font-semibold">
                  ⚠️ <strong>Importante:</strong> Antes de 01/04/2026, o sistema usa campos clássicos (ICMS/PIS/COFINS). 
                  A partir de 01/04, estas informações IBS/CBS/IPI serão utilizadas automaticamente.
                </p>
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-end pt-4 border-t">
              <div className="text-right space-y-1">
                <p className="text-sm font-medium text-muted-foreground">Valor Total:</p>
                <p className="text-3xl font-bold text-purple-600">
                  {formatCurrency({ value: valorTotal, currency: 'BRL' })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleEmitir} disabled={loading || items.length === 0}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Emitindo...
              </>
            ) : (
              'Emitir Nota Fiscal'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
